import { interpretarProximaVisita, type Pessoa } from '@sheafyield/dominio';
import { useId, useRef, useState } from 'react';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { Folha } from '../ds/Folha.js';
import {
  lerRascunhoDaFicha,
  gravarRascunhoDaFicha,
  limparRascunhoDaFicha,
} from './rascunhoDaFicha.js';

export function dataDaVisita(em: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(em));
}
function camposLocais(em: string | null) {
  if (em === null) return { data: '', hora: '' };
  const d = new Date(em);
  const dois = (n: number) => String(n).padStart(2, '0');
  return {
    data: `${String(d.getFullYear())}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`,
    hora: `${dois(d.getHours())}:${dois(d.getMinutes())}`,
  };
}
function instanteLocal(data: string, hora: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data) || !/^\d{2}:\d{2}$/.test(hora))
    return null;
  const instante = new Date(`${data}T${hora}:00`);
  if (Number.isNaN(instante.getTime())) return null;
  const em = instante.toISOString();
  const volta = camposLocais(em);
  // O Date normaliza 31/02 e horas inexistentes na mudança de fuso. Não
  // aceitamos que ele marque silenciosamente outro compromisso.
  return volta.data === data && volta.hora === hora ? em : null;
}
export function FolhaAgendar({
  pessoa,
  aoSalvar,
  aoFechar,
}: {
  pessoa: Pessoa;
  aoSalvar: (em: string) => Promise<void>;
  aoFechar: () => void;
}) {
  // Monta de novo a cada abertura. A referência temporal fica congelada:
  // virar meia-noite enquanto se lê a prévia não pode mudar o que se grava.
  const [rascunho] = useState(() => {
    const r = lerRascunhoDaFicha(pessoa, 'agendar');
    return r?.tipo === 'agendar' ? r : null;
  });
  const [agora] = useState(() =>
    rascunho === null ? new Date() : new Date(rascunho.agora),
  );
  const [texto, setTexto] = useState(rascunho?.texto ?? '');
  const [campos, setCampos] = useState(() =>
    rascunho === null
      ? camposLocais(pessoa.proxima_visita_em)
      : { data: rascunho.data, hora: rascunho.hora },
  );
  const [natural, setNatural] = useState(rascunho?.natural ?? false);
  function lembrar(
    texto: string,
    campos: { data: string; hora: string },
    natural: boolean,
  ) {
    gravarRascunhoDaFicha(pessoa, {
      tipo: 'agendar',
      texto,
      ...campos,
      agora: agora.toISOString(),
      natural,
    });
  }
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emCurso = useRef(false);
  const campo = useRef<HTMLFormElement>(null);
  const previaId = useId();
  const em = natural
    ? (interpretarProximaVisita(texto, agora)?.em ?? null)
    : instanteLocal(campos.data, campos.hora);
  async function salvar() {
    if (emCurso.current) return;
    if (em === null) {
      setErro('Confira a data e o horário. A marcação anterior será mantida.');
      return;
    }
    emCurso.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar(em);
      limparRascunhoDaFicha(pessoa, 'agendar');
    } catch (causa) {
      setErro(
        causa instanceof Error
          ? causa.message
          : 'Não deu para agendar. Tente novamente.',
      );
    } finally {
      emCurso.current = false;
      setSalvando(false);
    }
  }
  return (
    <Folha
      aberta
      titulo="Agendar visita"
      aoFechar={() => {
        if (emCurso.current) return false;
        return aoFechar();
      }}
      focoInicial={campo}
    >
      <form
        ref={campo}
        tabIndex={-1}
        className="ficha__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          void salvar();
        }}
      >
        <p>{pessoa.nome.trim() || pessoa.endereco || 'Pessoa sem nome'}</p>
        <Campo
          rotulo="Próxima visita"
          placeholder="sábado 10h"
          value={texto}
          disabled={salvando}
          aria-describedby={previaId}
          onChange={(evento) => {
            const novo = evento.target.value;
            setTexto(novo);
            setNatural(true);
            setErro(null);
            const entendida = interpretarProximaVisita(novo, agora);
            const novosCampos =
              entendida === null ? campos : camposLocais(entendida.em);
            setCampos(novosCampos);
            lembrar(novo, novosCampos, true);
          }}
        />
        <p className="ficha__previa" id={previaId} role="status">
          {em === null
            ? 'Informe uma data e um horário válidos.'
            : dataDaVisita(em)}
        </p>
        <div className="ficha__contato">
          <Campo
            rotulo="Data"
            type="date"
            value={campos.data}
            disabled={salvando}
            onChange={(evento) => {
              const novos = { ...campos, data: evento.target.value };
              setCampos(novos);
              lembrar('', novos, false);
              setNatural(false);
              setTexto('');
              setErro(null);
            }}
          />
          <Campo
            rotulo="Horário"
            type="time"
            value={campos.hora}
            disabled={salvando}
            onChange={(evento) => {
              const novos = { ...campos, hora: evento.target.value };
              setCampos(novos);
              lembrar('', novos, false);
              setNatural(false);
              setTexto('');
              setErro(null);
            }}
          />
        </div>
        {erro === null ? null : <p role="alert">{erro}</p>}
        <Botao type="submit" largura="cheia" disabled={salvando}>
          {salvando ? 'Agendando…' : 'Agendar'}
        </Botao>
      </form>
    </Folha>
  );
}
